/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react';
import {GenericObject} from '@ion/core/src/types/entities';
import RootManager, {
  ClientRootManager,
  Container,
  Renderable
} from './RootManager';

/**
 * Defines the {@link useRootManager} API which is based on a wrapper around the
 * `RootManager` instance API, as well as convenience aliases to a subset of static
 * {@link RootManager} APIs.
 *
 * @param render {@link RootManager.render}
 * @param unmount {@link RootManager.unmount}
 * @param destruct {@link RootManager.destruct}
 * @param createContainer {@link RootManager.createContainer}
 * @param removeContainer {@link RootManager.removeContainer}
 * @param getContainer {@link RootManager.getContainer}
 */
export type UseRootManagerResult = Partial<ClientRootManager> & {
  createContainer: (attributes?: string | GenericObject, el?: string) => HTMLElement;
  getContainer: (id: string) => HTMLElement | null;
  removeContainer: (container: Container) => void;
}

/**
 * The `useRootManager` hook provides an abstraction around the {@link RootManager}
 * API in order to simplify component implementations by providing a wrapper around
 * the underlying `RootManager` instance, as well as convenience aliases to the static
 * `createContainer`, `removeContainer`, and `getContainer` `RootManager` methods.
 *
 * Essentially, this hook wraps both the instance and static {@link RootManager} APIs
 * and manages destructing the root manager client root instances automatically.
 *
 * **Components (.tsx) modules are always to use the `useRootManager` hook as opposed
 * to using the {@link RootManager | RootManager API} directly.**
 *
 * ```typescript
 *   import {useRootManager} from '@ion/dls/src/client';
 *
 *   const {render, createContainer} = useRootManager();
 *
 *   ...
 *
 *   // create or reference an existing client root dom container ...
 *   const container = createContainer('some-container-id');
 *
 *   // render a component to the client root container ...
 *   render(<Component ... /> container);
 *
 *   // unmount a component from the client root container ...
 *   unmount(<Component ... /> container);
 * ```
 *
 * @param   destructOnUnmount When `true` (default), the `destruct` method will be
 *          invoked on the `RootManager` instance when the component unmounts.
 * @param   removeOnly When `true` (default), the `destruct` method will be invoked
 *          to only remove the container node from the dom (and not invoke unmount
 *          on the given root). This is the default behavior and should be used for
 *          the majority of use-cases as React will natively manage unmounting of
 *          roots when a component unmounts.
 * @returns An object containing the `RootManager` instance API, as well as well
 *          as convenience aliases to the static `createContainer` and `getContainer`,
 *          and `removeContainer` methods.
 */
export const useRootManager = (destructOnUnmount = true, removeOnly = true): UseRootManagerResult => {
  const roots = useRef<RootManager>(new RootManager());

  useEffect(() => {
    if (destructOnUnmount) {
      return () => {
        roots.current.destruct?.(removeOnly);
      };
    }
  }, [destructOnUnmount, removeOnly]);

  const render = useCallback((component: Renderable, container: Container) => {
    roots.current.render?.(component, container);
  }, []);

  const unmount = useCallback((container: Container, remove = true): boolean => {
    return roots.current.unmount?.(container, remove);
  }, []);

  const hasRoots = useCallback((): boolean => {
    return roots.current.hasRoots?.();
  }, []);

  const api = useMemo((): UseRootManagerResult => ({
    createContainer : RootManager.createContainer,
    removeContainer : RootManager.removeContainer,
    getContainer    : RootManager.getContainer,
    render,
    unmount,
    hasRoots
  }), []);

  return api;
};
export default useRootManager;
